import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import MuiPopover from "@material-ui/core/Popover";
import Tooltip from "@material-ui/core/Tooltip";
import CloseIcon from "@material-ui/icons/Close";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import MoreVertOutlinedIcon from "@material-ui/icons/MoreVertOutlined";
import RefreshIcon from "@material-ui/icons/Refresh";
import ThumbDownOutlinedIcon from "@material-ui/icons/ThumbDownOutlined";
import ThumbUpOutlinedIcon from "@material-ui/icons/ThumbUpOutlined";
import styles from "./index.module.scss";

const AiIcon = () => (
  <svg
    style={{ marginRight: "5px" }}
    width="15"
    height="18"
    viewBox="0 0 15 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M13.3333 0H1.66667C0.75 0 0 0.75 0 1.66667V13.3333C0 14.25 0.75 15 1.66667 15H5L7.5 17.5L10 15H13.3333C14.25 15 15 14.25 15 13.3333V1.66667C15 0.75 14.25 0 13.3333 0ZM13.3333 13.3333H9.30833L7.5 15.1417L5.69167 13.3333H1.66667V1.66667H13.3333V13.3333ZM7.5 12.5L9.06667 9.06667L12.5 7.5L9.06667 5.93333L7.5 2.5L5.93333 5.93333L2.5 7.5L5.93333 9.06667L7.5 12.5Z"
      fill="#0A859A"
    />
  </svg>
);

const Cursor = () => <span className={styles.cursor}>▋</span>;

export default function AiPopover({
  open,
  anchorEl,
  onClose,
  onFeedback,
  onRefresh,
  onDisableAI,
  children,
  className,
  anchorOrigin = {
    vertical: "top",
    horizontal: "right",
  },
  transformOrigin = {
    vertical: "top",
    horizontal: "left",
  },
  nudgeText = "",
  // Unused since the compliment auto-close was removed; kept out of ...props
  // so it isn't passed on to MuiPopover.
  nudgeType = "",
  streaming = false,
  error = null,
  onCloseError,
  ...props
}) {
  const rect = anchorEl?.getBoundingClientRect();
  const { t } = useTranslation();
  const [disableAiAnchorEl, setDisableAiAnchorEl] = useState(null);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  // The popover only closes when the coach closes it, compliments included
  // (COACH-2161). Closing clears the nudge state straight away, so keep
  // rendering the last content until the fade-out finishes instead of
  // fading out an empty box.
  const lastContentRef = useRef({ nudgeText, streaming, error });
  if (open) lastContentRef.current = { nudgeText, streaming, error };
  const shown = open ? { nudgeText, streaming, error } : lastContentRef.current;
  const showContent = shown.nudgeText || shown.streaming || shown.error;
  const disableAiOpen = Boolean(disableAiAnchorEl);
  const anchorPosition = rect
    ? { top: rect.top, left: rect.right + 10 }
    : undefined;

  return (
    <>
      <MuiPopover
        className={`${styles.popover} ${className || ""}`}
        open={open}
        onClose={onClose}
        elevation={4}
        transformOrigin={transformOrigin}
        anchorPosition={anchorPosition}
        anchorReference="anchorPosition"
        marginThreshold={0}
        {...props}
      >
        <div className={styles.contentWrapper}>
          <div className={styles.container}>
            <div className={styles.firstLine}>
              <div className={styles.aiLabel}>
                <AiIcon />
                {t("aiPopover.label")}
                <Tooltip
                  title={t("aiPopover.tooltip")}
                  classes={{ popper: styles.tooltipPopper }}
                  placement="top"
                >
                  <InfoOutlinedIcon
                    style={{ color: "#9E9E9E", fontSize: "13.33px" }}
                  />
                </Tooltip>
              </div>
              <CloseIcon
                style={{
                  color: "#9E9E9E",
                  fontSize: "24px",
                  cursor: "pointer",
                }}
                onClick={onClose}
              />
            </div>

            {shown.error ? (
              <div className={styles.errorLine}>
                <span style={{ fontSize: 16 }}>⚠</span>
                <span>{shown.error}</span>
                <button className={styles.retryButton} onClick={onCloseError}>
                  Retry
                </button>
              </div>
            ) : showContent ? (
              <div className={styles.secondLine}>
                <div className={styles.nudgeLabel}></div>
                {shown.streaming && !shown.nudgeText ? (
                  <div className={styles.skeletonContainer}>
                    <div
                      className={`${styles.skeletonLine} ${styles.medium}`}
                    />
                    <div className={`${styles.skeletonLine} ${styles.long}`} />
                    <div className={`${styles.skeletonLine} ${styles.short}`} />
                  </div>
                ) : (
                  <div className={styles.nudgeText}>
                    {shown.nudgeText}
                    {shown.streaming && <Cursor />}
                  </div>
                )}
              </div>
            ) : (
              <div className={styles.secondLine}>
                {/* Could you make it more specific? For example, setting a timeline like
                "within the next 30 days" */}
              </div>
            )}

            {!shown.error && showContent && (
              <div className={styles.lastLine}>
                <div className={styles.iconWrapper}>
                  <Tooltip
                    title={t("aiPopover.helpful")}
                    classes={{ popper: styles.tooltipPopper }}
                    placement="top"
                  >
                    <ThumbUpOutlinedIcon
                      className={`${styles.feedbackIcon} ${styles.thumbUp} ${selectedFeedback === "up" ? styles.selected : ""}`}
                      onClick={() => {
                        setSelectedFeedback("up");
                        onFeedback && onFeedback("up");
                      }}
                    />
                  </Tooltip>
                  <Tooltip
                    title={t("aiPopover.notHelpful")}
                    classes={{ popper: styles.tooltipPopper }}
                    placement="top"
                  >
                    <ThumbDownOutlinedIcon
                      className={`${styles.feedbackIcon} ${styles.thumbDown} ${selectedFeedback === "down" ? styles.selected : ""}`}
                      onClick={() => {
                        setSelectedFeedback("down");
                        onFeedback && onFeedback("down");
                      }}
                    />
                  </Tooltip>
                  <Tooltip
                    title={t("aiPopover.regenerate")}
                    classes={{ popper: styles.tooltipPopper }}
                    placement="top"
                  >
                    <RefreshIcon
                      style={{ color: "#9E9E9E", cursor: "pointer" }}
                      onClick={() => {
                        setSelectedFeedback(null);
                        onRefresh && onRefresh();
                      }}
                    />
                  </Tooltip>
                </div>
                <MoreVertOutlinedIcon
                  style={{ color: "#9E9E9E", cursor: "pointer" }}
                  onClick={e => setDisableAiAnchorEl(e.currentTarget)}
                />
              </div>
            )}
          </div>
        </div>
      </MuiPopover>

      <MuiPopover
        className={styles.disableAiPopover}
        open={disableAiOpen}
        anchorEl={disableAiAnchorEl}
        onClose={() => setDisableAiAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <div
          className={styles.disableAiOption}
          onClick={() => {
            onDisableAI && onDisableAI();
            setDisableAiAnchorEl(null);
          }}
        >
          Disable AI
        </div>
      </MuiPopover>
    </>
  );
}
